/**
 * ReadStatusBadge — Batch #14.55
 *
 * Badge visuel pour afficher l'état de lecture d'un document portail.
 *
 * Props :
 *   readAt: ISO string ou null/undefined → "Non lu"
 *   readCount: int (optionnel, défaut 0)
 *   size: "sm" (badge) | "md" (badge plus large)
 *
 * Usage :
 *   <ReadStatusBadge readAt={invoice.portal_read_at} readCount={invoice.portal_read_count} />
 */
import { Eye, MailOpen, Mail } from "lucide-react";

function _timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return "à l'instant";
  if (diffSec < 3600) return `il y a ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `il y a ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 2592000) return `il y a ${Math.floor(diffSec / 86400)} j`;
  return new Date(iso).toLocaleDateString("fr-CA");
}

export default function ReadStatusBadge({ readAt, readCount = 0, size = "sm", testId }) {
  const isRead = !!readAt;
  const cls = size === "md"
    ? "text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 font-medium border"
    : "text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-medium border";

  if (!isRead) {
    return (
      <span
        className={`${cls} bg-slate-50 text-slate-600 border-slate-200`}
        title="Pas encore consulté dans le portail"
        data-testid={testId || "read-badge-unread"}
      >
        <Mail className="w-3 h-3" />
        Non lu
      </span>
    );
  }

  const moreThanOnce = readCount > 1;
  return (
    <span
      className={`${cls} bg-emerald-50 text-emerald-700 border-emerald-200`}
      title={`Lu ${readCount}× — dernière : ${new Date(readAt).toLocaleString("fr-CA")}`}
      data-testid={testId || "read-badge-read"}
    >
      {moreThanOnce ? <Eye className="w-3 h-3" /> : <MailOpen className="w-3 h-3" />}
      Lu {_timeAgo(readAt)}
      {moreThanOnce && <span className="opacity-70">({readCount}×)</span>}
    </span>
  );
}
